// Shared chrome for the auth pages (LoginPage, ResetPasswordPage,
// VerifyEmailPage, ConfirmEmailChange, JoinWorkspace). They all render the
// same docked-form + drawing shell and the same password eye toggle; each
// used to hand-write its own copy, and the copies had already drifted (LoginPage wrapped the brand in
// a component still named after a removed shader). Components only in this
// file — shared non-component helpers live in utils/authStorage.js.
import { useEffect, useState } from 'react';
import drawingUrl from '../../assets/login-drawing.png';
import './LoginPage.css';

// Natural size of login-drawing.png (the website hero's P&ID). The SVG uses
// it as its coordinate space, so the boxes below are in drawing pixels and
// crop/scale with the drawing.
const DRAWING_W = 2432;
const DRAWING_H = 1074;

/**
 * Objects picked out on the drawing the way MainView draws detections:
 * class-coloured box, label tab above (or below). `circle` = instrument
 * bubble. In DRAWING pixels, measured off the sheet itself.
 *
 * WHERE THEY SIT. The card covers the middle of the sheet (roughly x
 * 1230–1730 at a desktop width), so everything here is outside that: a few
 * on the left, more down the right, and none stacked on top of each other.
 * It was six of them crowded into one corner of the left, three of them
 * overlapping, with the whole right half of the sheet bare.
 *
 * WHAT THEY ARE. Instruments, IO and a few valves — the things a reader
 * recognises as a P&ID's own vocabulary, plus one pump (700-PP-002, under
 * the card). Only one: a 148px box is a slab on a dark page, and several
 * were the loudest thing on a sign-in screen.
 */
const DETECTIONS = [
  // Left of the card, kept to two so the corner is not a pile.
  { cls: 'inst', x: 866, y: 347, w: 60, h: 60, circle: true, label: 'XV-7002 · Actuator', below: true },
  { cls: 'valve', x: 798, y: 806, w: 72, h: 50, label: '300BV · Valve' },
  // The one pump: 700-PP-002 sits just under the card. Label below it, so
  // the tab never reaches up into the card.
  { cls: 'pump', x: 1256, y: 760, w: 148, h: 144, label: '700-PP-002 · Pump', below: true },
  // Right of the card: the instrument loop and its IO, which is where the
  // sheet is busiest and nothing was marked at all.
  { cls: 'io', x: 2098, y: 205, w: 38, h: 43, label: 'FIC-7003 · Controller', flip: true },
  { cls: 'inst', x: 2097, y: 311, w: 40, h: 40, circle: true, label: 'FIT-7003 · Flow transmitter', flip: true, below: true },
  { cls: 'inst', x: 1765, y: 735, w: 40, h: 40, circle: true, label: 'XV-7006 · Actuator' },
  { cls: 'valve', x: 1690, y: 788, w: 56, h: 34, label: '250BV · Valve', below: true },
];

// THE GROUND, not a pane: the drawing fills the screen behind the form,
// dimmed by a veil heavy enough to read type over, with its objects
// fading in once, one after another, in the outer thirds where the card is
// not, then a spotlight moving slowly from one to the next (the website
// hero's motion and colours). The
// Objects list and the tool pill are gone with the split — controls that
// do nothing, behind a form you cannot dismiss, were the price of showing
// the app rather than the drawing. Decorative only.
// The spotlight, as the website's search picks out a tag (hero.tsx): one
// object at a time is lit, thicker outline and one slow pulse, while the
// rest dim. Here it never stops — it moves on every few seconds, after the
// objects have all faded in — so the ground is always quietly alive.
// With reduced motion nothing is lit and nothing moves.
const SPOT_START_MS = 3600;
const SPOT_EVERY_MS = 4800;

function useSpotlight(count) {
  const [spot, setSpot] = useState(-1);
  useEffect(() => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;
    let every;
    const start = setTimeout(() => {
      setSpot(0);
      every = setInterval(() => setSpot((i) => (i + 1) % count), SPOT_EVERY_MS);
    }, SPOT_START_MS);
    return () => { clearTimeout(start); clearInterval(every); };
  }, [count]);
  return spot;
}

function DrawingShowcase() {
  const spot = useSpotlight(DETECTIONS.length);
  return (
    <div className="login-show" aria-hidden="true">
      <div className="login-show-file"><b>700-PID-PR-10002</b><span>Concentrate transfer · Rev C</span></div>
      {/* The WHOLE sheet, panned so the detected objects land left of the
          card rather than behind it. It was cropped to a column's worth of
          drawing, which at this width read as a few huge symbols. */}
      <svg className="login-show-dwg" viewBox="520 0 1912 1074" preserveAspectRatio="xMidYMid slice">
        <image href={drawingUrl} width={DRAWING_W} height={DRAWING_H} className="login-show-img" />
        {DETECTIONS.map((d, i) => {
          const tabW = d.label.length * 11 + 22;
          const tabY = d.below ? d.y + d.h + 8 : d.y - 40;
          // A label near the right edge of the sheet runs off it. `flip`
          // hangs the tab back from the object's right side instead.
          const tabX = d.flip ? d.x + d.w - tabW + 2 : d.x - 2;
          // The outer group dims and lights; the inner one fades in once.
          // They are separate because the fade-in's fill-mode owns opacity.
          const state = spot < 0 ? '' : spot === i ? ' is-on' : ' is-dim';
          return (
            <g key={i} className={`login-spot${state}`}>
              <g className={`login-det login-det--${d.cls}`} style={{ animationDelay: `${0.5 + i * 0.3}s` }}>
                {d.circle
                  ? <circle cx={d.x + d.w / 2} cy={d.y + d.h / 2} r={d.w / 2} />
                  : <rect x={d.x} y={d.y} width={d.w} height={d.h} rx={5} />}
                <rect className="login-det-tab" x={tabX} y={tabY} width={tabW} height={32} rx={4} />
                <text x={tabX + 11} y={tabY + 23}>{d.label}</text>
                {/* Keyed on the visit so the pulse plays again each time the
                    spotlight comes back round. */}
                {spot === i && (d.circle
                  ? <circle key={`p${spot}`} className="login-pulse" cx={d.x + d.w / 2} cy={d.y + d.h / 2} r={d.w / 2} />
                  : <rect key={`p${spot}`} className="login-pulse" x={d.x} y={d.y} width={d.w} height={d.h} rx={5} />)}
              </g>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// The auth shell: the drawing is the whole ground, the form a card in the
// middle of it, the legal line along the foot. It was a white column docked
// left with the drawing beside it — a hard seam down the middle of a dark
// screen, and a drawing cropped into a column reads as decoration rather
// than as a drawing. On a phone the card is the page and the drawing simply
// sits behind it.
//
// Mock-ups: https://claude.ai/artifact/DBhGT66Dus9ULkJMtWZ1LQ
export function AuthPage({ children }) {
  return (
    <div className="login-page">
      <DrawingShowcase />
      <div className="login-veil" aria-hidden="true" />
      <div className="login-card">
        <div className="login-brand">pidly</div>
        <div className="login-container">
          {children}
        </div>
      </div>
      <LoginFoot />
    </div>
  );
}

function LoginFoot() {
  return (
    <footer className="login-foot">
      <nav className="login-foot-links" aria-label="Legal and support">
        <a href="https://pidly.com.au/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        <span className="login-foot-sep" aria-hidden="true">·</span>
        <a href="https://pidly.com.au/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
        <span className="login-foot-sep" aria-hidden="true">·</span>
        <a href="mailto:support@pidly.com.au">Support</a>
        <span className="login-foot-sep" aria-hidden="true">·</span>
        <a href="https://pidly.com.au" target="_blank" rel="noopener noreferrer">pidly.com.au</a>
      </nav>
      <div className="login-foot-copy">© 2026 Pidly</div>
    </footer>
  );
}

// Show/hide password toggle icon.
export function EyeIcon({ open }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {open ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
          <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </>
      )}
    </svg>
  );
}
